import type {
	BrandJobResponse,
	BrandProfile,
	BrandProfileResponse,
	CreateDocInput,
	DeckFolder,
	DeckModel,
	Doc,
	DocFormat,
	DocList,
	DocListQuery,
	ChatHistoryPage,
	SaveBrandProfileInput,
	SendTurnInput,
	SessionUser,
	ShareGroupInput,
	ShareInput,
	SlideTemplatesResponse,
	PendingTextEditsResponse,
	TextEditsRequest,
	TextEditsResponse,
	UpdateDocInput,
} from "@makerx-mp/shared";
import { ApiError, request, requestForm, type ApiRequestInit } from "./api-request.js";

const BASE = "/api";

/** The theme + layout the user is currently viewing, passed to the export
 *  endpoints so a PDF/PPTX/HTML export is WYSIWYG (matches the preview) without
 *  waiting on the debounced persist. Omitted keys fall back to docs.theme /
 *  docs.format server-side. */
export type DeckView = {
	theme?: "dark" | "light";
	format?: "landscape" | "portrait";
};
function viewQuery(view?: DeckView): string {
	if (!view) return "";
	const p = new URLSearchParams();
	if (view.theme) p.set("theme", view.theme);
	if (view.format) p.set("format", view.format);
	const s = p.toString();
	return s ? `?${s}` : "";
}

export interface DocWithRole extends Doc {
	role: "owner" | "editor" | "viewer";
	/** Whether the SIGNED-IN user has starred this deck (per-user, not a
	 *  document property — two people see different values on one deck). */
	isFavourite: boolean;
	/** Owner-only — total $ spent and total billable tokens (input +
	 *  output) consumed by assistant turns for this doc, plus how many
	 *  turns produced a billable result. Editors/viewers don't receive
	 *  this field; the SPA hides the usage line when missing. The editor
	 *  shows tokens (b2c) or USD (enterprise) from this rollup. */
	cost?: { totalUsd: number; turnCount: number; totalTokens: number };
	/** The deck's conversation holds images, so the selector offers only
	 *  models that read them. */
	imageHistory?: boolean;
	/** b2c with billing on: which models the deck's OWN team may run — the
	 *  premium ones (a paid plan) and the standard one (until a paid team
	 *  reaches its monthly fair-use limit, which lifts at
	 *  `standardResumesAt`) — and whether that team is the viewer's active one,
	 *  so its billing page is the place to upgrade. Absent where no plan
	 *  applies. */
	modelAccess?: {
		premium: boolean;
		standard: boolean;
		standardResumesAt: string | null;
		upgradeHere: boolean;
	};
}

export interface DocVersion {
	id: string;
	source: string; // "agent_turn" | "seed" | "restore"
	createdAt: string;
	createdBy: { id: string; name: string; email: string } | null;
	bytes: number;
}

export interface DocCompareResult {
	after: DocVersion;
	afterHtml: string;
	before: DocVersion | null;
	beforeHtml: string | null;
}

// ---- Brand Studio (docs/plans/brand-studio.md) --------------------------
export interface StudioTemplate {
	id: string;
	key: string;
	name: string;
	description: string | null;
	format: "landscape" | "portrait";
	sortOrder: number;
	updatedAt: string;
}

export interface StudioGroup {
	id: string;
	key: string;
	name: string;
	description: string | null;
	kind: string;
	sortOrder: number;
	/** The deck a slide library (`kind: "library"`) was saved from. */
	sourceDocId: string | null;
	updatedAt: string;
	/** The group's canvas doc id, when one has been materialised. */
	canvasDocId: string | null;
	templates: StudioTemplate[];
}

export interface BrandGroupsResponse {
	canEdit: boolean;
	groups: StudioGroup[];
}

/** A style a NEW deck can be built from (GET /brand/deck-styles): one of the
 *  team's template groups, with a few live tile previews for the picker. */
export interface DeckStyle {
	id: string;
	key: string;
	name: string;
	description: string | null;
	templateCount: number;
	previews: Array<{
		key: string;
		name: string;
		format: "landscape" | "portrait";
		html: string;
	}>;
}

export interface StudioVersionSummary {
	added: string[];
	removed: string[];
	modified: string[];
	restoredFrom?: string;
}

export interface StudioVersion {
	id: string;
	summary: StudioVersionSummary | null;
	createdAt: string;
	createdBy: { id: string; name: string } | null;
	bytes: number;
}

export interface HealthTemplateRef {
	id: string;
	key: string;
	name: string;
	groupId: string | null;
	/** "foundation" is the deck shell: edited in its canvas, never retokenised. */
	groupKind: string | null;
	count?: number;
}
export interface BrandHealth {
	hardcodedColours: { templates: HealthTemplateRef[]; total: number };
	darkThemeGap: { templates: HealthTemplateRef[] };
	logoVariants: { hasLight: boolean; hasDark: boolean };
	/** Brand fonts we can't serve, and what renders instead. */
	fontSubstitutions: Array<{
		role: "display" | "body";
		requested: string;
		servedAs: string;
		reason: string;
	}>;
}

export interface BrandMaterial {
	id: string;
	kind: string;
	name: string | null;
	mediaType: string | null;
	sizeBytes: number | null;
	sourceUrl: string | null;
	createdAt: string;
	/** A file the deck agent can open (an upload or an import's retained
	 *  file), as opposed to a derived artefact such as a website screenshot. */
	readable: boolean;
}
/** What `POST /brand/materials` kept: documents as shared files, images and
 *  fonts forwarded to the asset library. */
export interface MaterialsUploadResult {
	materials: Array<{
		id: string;
		kind: string;
		name: string;
		mediaType: string | null;
		sizeBytes: number | null;
		createdAt: string;
	}>;
	/** How many of the documents replaced an existing file of the same name. */
	replaced: number;
	assets: Array<{ name: string; token: string; family?: string }>;
}
export interface BrandImportJob {
	id: string;
	status: string;
	progress: string[];
	result: unknown;
	error: string | null;
	createdAt: string;
}
export interface BrandMaterials {
	canEdit: boolean;
	materials: BrandMaterial[];
	imports: BrandImportJob[];
}

export interface BrandLibraryAsset {
	id: string;
	name: string;
	/** The `{{BRAND_ASSET:name}}` token to reference it from a template/deck. */
	token: string;
	mediaType: string;
	sizeBytes: number;
	kind: string;
	createdAt: string;
	/** Bumped by a same-name re-upload; keys the preview URL. */
	updatedAt: string;
}
export interface BrandAssetLibrary {
	canEdit: boolean;
	assets: BrandLibraryAsset[];
}

export interface AccessUser {
	userId: string;
	email: string;
	name: string;
	role: "owner" | "editor" | "viewer";
}

export interface SearchedUser {
	id: string;
	email: string;
	name: string;
}

export interface SearchedGroup {
	id: string;
	name: string;
	description?: string;
}

export interface AccessGroup {
	groupId: string;
	groupName: string;
	role: "editor" | "viewer";
}

/** A message from a PREVIOUS conversation on a deck — kept verbatim when
 *  someone started a new chat, and read back on request. */
export interface ArchivedChatMessage {
	id: string;
	role: "user" | "assistant" | "system";
	authorId: string | null;
	authorName: string | null;
	text: string;
	createdAt: string;
	archivedAt: string;
}

export interface RenderResult {
	id: string;
	status: "pending" | "rendering" | "succeeded" | "failed";
	blobUrl: string | null;
	errorMessage: string | null;
	createdAt: string;
	completedAt: string | null;
}

export interface AppConfig {
	authMode: "entra" | "dev" | "clerk";
	agentMode: "real" | "fake";
	/** b2c only — present when authMode === "clerk". Used to mount ClerkProvider. */
	clerkPublishableKey?: string;
	/** Whether the Stripe billing surface should be shown (b2c). */
	stripeEnabled: boolean;
	/** When true, voice input streams to Azure STT (via /api/speech/token);
	 *  when false the composer falls back to the browser Web Speech API. */
	speechEnabled: boolean;
	/** When true the /team page offers email-domain claiming (still
	 *  enterprise-only behind that). Off unless the edition switches it on. */
	orgDomainsEnabled: boolean;
	/** How much of the Brand Studio this edition has. "full" (b2c) is a team's
	 *  whole brand; "templates" is one instance-wide template library, managed
	 *  by admins and used by everyone, with no brand profile behind it. */
	brandScope: "full" | "templates";
	/** When true the composer shows the per-deck model selector in place of the
	 *  keyboard hint. Optional so an older server's payload still parses —
	 *  absent means off. */
	deckModelsEnabled?: boolean;
	/** The models offered by the selector, in display order — the selectable
	 *  catalogue only, so every entry can be picked. Empty when the edition
	 *  can't choose one. */
	deckModels?: DeckModel[];
	/** The id new decks are created on; null when the edition has no selector. */
	defaultDeckModel?: string | null;
	/** The model a deck with no recorded choice runs on: the deployment's
	 *  own. The selector shows it for those decks so a deck always reads as a
	 *  concrete model. On b2c it is also the standard model, which a paid plan
	 *  runs outside its allowance. Null without a selector. */
	fallbackDeckModel?: string | null;
}

export type TeamRole = "admin" | "member";

/** "preload": an unclaimed preloaded brand an admin is working in (Admin
 *  Mode) — MakerX's until the first signup from its domain claims it. */
export type TeamKind = "team" | "preload";

export interface Team {
	id: string;
	name: string;
	role: TeamRole;
	kind: TeamKind;
}

/** The signed-in user plus system-admin state. `impersonator` is non-null
 *  only while an admin is "viewing as" this user (drives the banner).
 *  `activeOrg` is the session's active team (with the caller's role) and
 *  `teams` the full list — both server truth, driving the native header
 *  switcher and the team gates. `pendingInviteCount` badges invitations
 *  addressed to the caller's email. */
export interface Me extends SessionUser {
	isAdmin: boolean;
	/** The hardcoded super-admin (may impersonate admins + demote them). */
	isSuperAdmin: boolean;
	impersonator: { id: string; email: string; name: string } | null;
	activeOrg: {
		id: string;
		name: string;
		role: TeamRole | null;
		kind: TeamKind;
		/** The preloaded brand's domain, when `kind` is "preload". */
		preloadDomain: string | null;
	} | null;
	teams: Team[];
	pendingInviteCount: number;
}

export interface TeamMember {
	userId: string;
	email: string;
	name: string;
	role: TeamRole;
	joinedAt: string;
	isOwner: boolean;
	/** Invited/imported but has never signed in yet. */
	pending: boolean;
}

export interface TeamSeats {
	members: number;
	pendingInvites: number;
}

export interface TeamInvite {
	id: string;
	email: string;
	role: TeamRole;
	kind: "invite" | "request";
	status: string;
	expiresAt: string;
	createdAt: string;
	invitedByName: string | null;
}

export interface MyInvite {
	id: string;
	orgId: string;
	orgName: string;
	role: TeamRole;
	inviterName: string | null;
}

export interface TeamSuggestion {
	orgId: string;
	orgName: string;
	mode: "auto_join" | "suggest";
}

export interface DomainVerificationRecord {
	type: "TXT";
	/** Fully-qualified host to create the record on. */
	host: string;
	value: string;
}

export interface TeamDomain {
	domain: string;
	mode: "auto_join" | "suggest";
	/** Until this is true the claim is INERT — nobody joins or is suggested
	 *  the team on the strength of it. */
	verified: boolean;
	verifiedAt: string | null;
	/** Last DNS check, successful or not. */
	lastCheckedAt: string | null;
	createdAt: string;
	/** The DNS record that proves control of the domain. */
	record: DomainVerificationRecord;
}

export interface InvitePreview {
	orgName: string;
	email: string;
	role: TeamRole;
	inviterName: string | null;
	expired: boolean;
}

export interface AdminStats {
	totalUsers: number;
	deletedUsers: number;
	newUsers30d: number;
	activeUsers7d: number;
	activeUsers30d: number;
	totalDocs: number;
	totalOrgs: number;
	totalTokens: number;
}

export interface AdminSeriesPoint {
	date: string;
	count: number;
}

export interface AdminTopOrg {
	orgId: string;
	name: string;
	tokens: number;
	subscriptionStatus: string | null;
}

export interface AdminOverview {
	stats: AdminStats;
	signups: AdminSeriesPoint[];
	docsCreated: AdminSeriesPoint[];
	topOrgs: AdminTopOrg[];
}

export interface AdminUser {
	id: string;
	email: string;
	name: string;
	createdAt: string;
	deletedAt: string | null;
	docCount: number;
	lastActiveAt: string | null;
	isAdmin: boolean;
	isSuperAdmin: boolean;
}

export interface AdminOrg {
	orgId: string;
	name: string;
	memberCount: number;
	docCount: number;
	tokens: number;
	subscriptionStatus: string | null;
	brandStatus: string | null;
	/** The org owner (Clerk creator / first admin), resolved to a name + email;
	 *  null until captured/backfilled or if the owner isn't in our user cache. */
	owner: { name: string; email: string } | null;
	createdAt: string | null;
}

export interface AdminUserDetail {
	user: {
		id: string;
		email: string;
		name: string;
		createdAt: string;
		deletedAt: string | null;
		isAdmin: boolean;
		isSuperAdmin: boolean;
		provider: "clerk" | "dev" | "entra";
		subject: string;
	};
	stats: {
		docCount: number;
		sessionCount: number;
		lastActiveAt: string | null;
	};
	teams: Array<{ orgId: string; name: string }>;
}

export interface AdminOrgDetail {
	org: { orgId: string; name: string; docCount: number };
	brand: {
		companyName: string;
		tagline: string | null;
		toneOfVoice: string | null;
		websiteUrl: string | null;
		status: string;
		removeMakerXName: boolean;
		/** Renderable logo (data URI) for preview, or null. */
		logo: string | null;
		needsLogo: boolean;
	} | null;
	billing: {
		subscriptionStatus: string | null;
		meteredTokens: number;
		periodStartedAt: string | null;
		freeAllowanceOverride: number | null;
		monthlyCapOverride: number | null;
		stripeCustomerId: string | null;
		stripeSubscriptionId: string | null;
	};
	usage: {
		planId: PlanId;
		periodTokens: number;
		allowance: number;
		creditBalance: number;
	};
	members: Array<{ id: string; email: string; name: string }>;
}

export interface AdminBrandAsset {
	id: string;
	kind: string;
	originalName: string | null;
	mediaType: string | null;
	sizeBytes: number | null;
	sourceUrl: string | null;
	createdAt: string;
}

export interface AdminBrandAssets {
	assets: AdminBrandAsset[];
	websiteUrl: string | null;
	brandStatus: string | null;
	hasProfile: boolean;
	processing: { id: string; status: string } | null;
}

export interface AdminBrandJob {
	id: string;
	status: "queued" | "running" | "succeeded" | "failed";
	insights: string[];
	error: string | null;
}

export interface AdminPreloadRow {
	domain: string;
	displayName: string | null;
	status: "pending" | "running" | "complete" | "failed";
	segments: {
		scan: boolean;
		logo: boolean;
		profile: boolean;
		templates: boolean;
	};
	logoThumb: string | null;
	error: string | null;
	ageMs: number | null;
	editedAt: string | null;
}

export interface AdminUserPatch {
	name?: string;
	email?: string;
}

export interface AdminOrgPatch {
	brand?: {
		companyName?: string;
		tagline?: string | null;
		toneOfVoice?: string | null;
		websiteUrl?: string | null;
		status?: string;
		removeMakerXName?: boolean;
	};
	billing?: {
		subscriptionStatus?: string | null;
		freeAllowanceOverride?: number | null;
		monthlyCapOverride?: number | null;
		/** Operator plan override (drives allowance + native seat limit). */
		planId?: string;
	};
}

export interface PendingPlanChange {
	planId: PlanId;
	/** ISO timestamp the change takes effect (the current period end). */
	effectiveAt: string;
}

export interface BillingStatus {
	stripeEnabled: boolean;
	planId?: PlanId;
	subscriptionStatus: string | null;
	hasSubscription: boolean;
	/** False when the user has no active team selected. */
	activeTeam: boolean;
	/** True when the caller is an admin of the active team (can manage billing). */
	isAdmin?: boolean;
	/** A deferred downgrade scheduled for period end, or null when none pending. */
	pendingChange?: PendingPlanChange | null;
	/** Seat consumption vs the plan's limit (null limit = unlimited). Pending
	 *  invites hold a seat. */
	seats?: { used: number; pendingInvites: number; limit: number | null };
}

export interface MemberUsage {
	userId: string | null;
	name: string;
	tokens: number;
}

export interface AppNotification {
	id: string;
	type: string;
	title: string;
	body: string | null;
	link: string | null;
	readAt: string | null;
	createdAt: string;
}

export interface Invoice {
	id: string;
	status: string | null;
	total: number;
	currency: string;
	created: string;
	hostedUrl: string | null;
	pdfUrl: string | null;
}

export type PlanId = "free" | "starter" | "team" | "scale" | "enterprise";

export interface AutoReloadConfig {
	enabled: boolean;
	thresholdTokens: number | null;
	/** USD charged per auto top-up (tokens derived at the credit rate). */
	amountUsd: string | null;
	monthlyCapUsd: string | null;
}

export interface BillingUsage {
	activeTeam: boolean;
	/** All fields below are present when activeTeam is true. */
	planId?: PlanId;
	periodTokens?: number;
	/** Monthly included token allowance (plan default or per-org override). */
	allowance?: number;
	/** This period's standard-model usage on a paid plan, and the fair-use
	 *  limit it runs to before drawing on the allowance (0 on free). */
	standardTokens?: number;
	standardFairUse?: number;
	/** When the period rolls over (ISO); null on free. */
	periodEndsAt?: string | null;
	/** Remaining prepaid credit tokens (drawn on after the allowance is spent). */
	creditBalance?: number;
	subscriptionStatus?: string | null;
	periodStartedAt?: string | null;
	autoReload?: AutoReloadConfig;
	/** Tokens assumed per "typical" document — used for "≈ N documents" estimates. */
	typicalDocTokens?: number;
}

export interface PlanCatalogEntry {
	id: PlanId;
	label: string;
	priceUsd: number;
	/** Monthly token allowance; null = unlimited (enterprise). */
	tokens: number | null;
	/** Illustrative document estimate; null when tokens is null. */
	docs: number | null;
	/** Seat cap; null = unlimited. */
	seats: number | null;
	selfServe: boolean;
	credits: boolean;
}

export interface CreditRate {
	/** USD per 1,000,000 tokens — the fixed credit top-up rate. */
	usdPerMtok: number;
	minUsd: number;
	maxUsd: number;
}

export interface BillingCatalog {
	plans: PlanCatalogEntry[];
	credit: CreditRate;
	typicalDocTokens: number;
}

export const api = {
	config: async (init?: ApiRequestInit) => {
		const config = await request<AppConfig>("/config", init);
		if (
			!config || typeof config !== "object" ||
			!["entra", "dev", "clerk"].includes(config.authMode) ||
			(config.authMode === "clerk" && (typeof config.clerkPublishableKey !== "string" || !config.clerkPublishableKey.trim()))
		) {
			throw new ApiError(200, "invalid_response", "The service returned an invalid configuration. Please try again.");
		}
		return config;
	},
	/** Mint a short-lived Azure Speech token for browser STT (voice input). */
	speechToken: () =>
		request<{ token: string; region: string }>("/speech/token"),
	me: () => request<Me>("/me"),
	logout: () => request<void>("/auth/logout", { method: "POST" }),
	// ---- system admin (/admin) ----------------------------------------------
	adminOverview: () => request<AdminOverview>("/admin/overview"),
	adminUsers: (q?: string) =>
		request<{ users: AdminUser[] }>(
			`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`,
		),
	adminOrgs: () => request<{ orgs: AdminOrg[] }>("/admin/orgs"),
	adminUserDetail: (id: string) =>
		request<AdminUserDetail>(`/admin/users/${id}`),
	adminUpdateUser: (id: string, patch: AdminUserPatch) =>
		request<{ ok: true; user: SessionUser }>(`/admin/users/${id}`, {
			method: "PATCH",
			body: JSON.stringify(patch),
		}),
	adminCloseUser: (id: string) =>
		request<{ ok: true }>(`/admin/users/${id}/close`, { method: "POST" }),
	adminRestoreUser: (id: string) =>
		request<{ ok: true }>(`/admin/users/${id}/restore`, { method: "POST" }),
	adminDemoteUser: (id: string) =>
		request<{ ok: true }>(`/admin/users/${id}/demote`, { method: "POST" }),
	adminPromoteUser: (id: string) =>
		request<{ ok: true }>(`/admin/users/${id}/promote`, { method: "POST" }),
	adminRevokeUserSessions: (id: string) =>
		request<{ ok: true; revoked: number }>(
			`/admin/users/${id}/revoke-sessions`,
			{
				method: "POST",
			},
		),
	adminOrgDetail: (id: string) => request<AdminOrgDetail>(`/admin/orgs/${id}`),
	adminUpdateOrg: (id: string, patch: AdminOrgPatch) =>
		request<{ ok: true }>(`/admin/orgs/${id}`, {
			method: "PATCH",
			body: JSON.stringify(patch),
		}),
	adminResetOrgUsage: (id: string) =>
		request<{ ok: true }>(`/admin/orgs/${id}/reset-usage`, { method: "POST" }),
	adminBrandAssets: (id: string) =>
		request<AdminBrandAssets>(`/admin/orgs/${id}/brand-assets`),
	adminSetOrgLogo: (id: string, form: FormData) =>
		requestForm<{ ok: true; logo: string | null }>(
			`/admin/orgs/${id}/logo`,
			form,
		),
	adminAddBrandAssets: (id: string, form: FormData) =>
		requestForm<{ ok: true; added: number }>(
			`/admin/orgs/${id}/brand-assets`,
			form,
		),
	adminDeleteBrandAsset: (id: string, assetId: string) =>
		request<{ ok: true }>(`/admin/orgs/${id}/brand-assets/${assetId}`, {
			method: "DELETE",
		}),
	adminReprocessBrand: (id: string, url?: string) =>
		request<{ id: string }>(`/admin/orgs/${id}/reprocess-brand`, {
			method: "POST",
			body: JSON.stringify({ url }),
		}),
	adminBrandJob: (id: string, jobId: string) =>
		request<AdminBrandJob>(`/admin/orgs/${id}/brand-job/${jobId}`),
	// ---- preload brands ----
	adminPreloadList: () =>
		request<{ rows: AdminPreloadRow[] }>("/admin/preload"),
	adminPreloadCreate: (prompt: string, limit?: number, force?: boolean) =>
		request<{
			batchId: string | null;
			queued: string[];
			skipped: Array<{ domain: string; reason: string }>;
		}>("/admin/preload", {
			method: "POST",
			body: JSON.stringify({ prompt, limit, force }),
		}),
	/** Admin Mode: join the preloaded brand's org and make it the active team. */
	adminPreloadEnter: (domain: string) =>
		request<{ orgId: string }>(
			`/admin/preload/${encodeURIComponent(domain)}/enter`,
			{ method: "POST" },
		),
	adminPreloadLeave: (domain: string) =>
		request<{ activeOrgId: string | null }>(
			`/admin/preload/${encodeURIComponent(domain)}/leave`,
			{ method: "POST" },
		),
	/** Rebuild. An edited brand needs `discardEdits` (409 `edited` otherwise). */
	adminPreloadRefresh: (domain: string, discardEdits = false) =>
		request<{ ok: true }>(
			`/admin/preload/${encodeURIComponent(domain)}/refresh`,
			{ method: "POST", body: JSON.stringify({ discardEdits }) },
		),
	adminPreloadDelete: (domain: string) =>
		request<{ ok: true }>(`/admin/preload/${encodeURIComponent(domain)}`, {
			method: "DELETE",
		}),
	adminImpersonationContext: () =>
		request<{ mfaRequired: boolean }>("/admin/impersonation-context"),
	adminImpersonate: (userId: string, mfaCode?: string) =>
		request<{ ok: true; user: SessionUser }>("/admin/impersonate", {
			method: "POST",
			body: JSON.stringify({ userId, mfaCode }),
		}),
	stopImpersonating: () =>
		request<{ ok: true; user: SessionUser }>("/admin/stop-impersonating", {
			method: "POST",
		}),
	/** b2c: exchange a Clerk session token for an mx_session cookie. An
	 *  invite token (from /invite/$token) is accepted atomically during the
	 *  mint so a brand-new invitee lands in the right team, not an
	 *  auto-provisioned workspace; `inviteAccepted` reports that outcome. */
	clerkLogin: (clerkToken: string, opts?: { inviteToken?: string }) =>
		request<{ inviteAccepted?: boolean }>("/auth/clerk", {
			method: "POST",
			// The gate waits on this one call; a lost response must surface as
			// an error with Try again, not as a spinner for three minutes.
			timeoutMs: 20_000,
			headers: { Authorization: `Bearer ${clerkToken}` },
			body: opts?.inviteToken
				? JSON.stringify({ inviteToken: opts.inviteToken })
				: undefined,
		}),
	// ---- native team management (b2c) ---------------------------------------
	teams: () => request<{ teams: Team[] }>("/orgs"),
	createTeam: (name: string) =>
		request<{ id: string; name: string }>("/orgs", {
			method: "POST",
			body: JSON.stringify({ name }),
		}),
	renameTeam: (name: string) =>
		request<{ ok: true; name: string }>("/orgs/current", {
			method: "PATCH",
			body: JSON.stringify({ name }),
		}),
	deleteTeam: () =>
		request<{ ok: true; detachedDocs: number }>("/orgs/current", {
			method: "DELETE",
		}),
	transferTeamOwnership: (userId: string) =>
		request<{ ok: true }>("/orgs/current/owner", {
			method: "POST",
			body: JSON.stringify({ userId }),
		}),
	teamMembers: () =>
		request<{ members: TeamMember[]; seats: TeamSeats }>(
			"/orgs/current/members",
		),
	setTeamMemberRole: (userId: string, role: TeamRole) =>
		request<{ ok: true }>(`/orgs/current/members/${userId}`, {
			method: "PATCH",
			body: JSON.stringify({ role }),
		}),
	removeTeamMember: (userId: string) =>
		request<{ ok: true }>(`/orgs/current/members/${userId}`, {
			method: "DELETE",
		}),
	teamInvites: () => request<{ invites: TeamInvite[] }>("/orgs/current/invites"),
	createTeamInvite: (email: string, role: TeamRole) =>
		request<{ invite: { id: string; email: string; role: TeamRole } }>(
			"/orgs/current/invites",
			{ method: "POST", body: JSON.stringify({ email, role }) },
		),
	resendTeamInvite: (id: string) =>
		request<{ ok: true }>(`/orgs/current/invites/${id}/resend`, {
			method: "POST",
		}),
	approveJoinRequest: (id: string) =>
		request<{ ok: true }>(`/orgs/current/invites/${id}/approve`, {
			method: "POST",
		}),
	revokeTeamInvite: (id: string) =>
		request<{ ok: true }>(`/orgs/current/invites/${id}`, { method: "DELETE" }),
	teamDomains: () => request<{ domains: TeamDomain[] }>("/orgs/current/domains"),
	addTeamDomain: (domain: string, mode: "auto_join" | "suggest") =>
		request<{ domain: Pick<TeamDomain, "domain" | "mode" | "record"> }>(
			"/orgs/current/domains",
			{ method: "POST", body: JSON.stringify({ domain, mode }) },
		),
	verifyTeamDomain: (domain: string) =>
		request<{ ok: true; verifiedAt: string }>(
			`/orgs/current/domains/${encodeURIComponent(domain)}/verify`,
			{ method: "POST" },
		),
	removeTeamDomain: (domain: string) =>
		request<{ ok: true }>(
			`/orgs/current/domains/${encodeURIComponent(domain)}`,
			{ method: "DELETE" },
		),
	myInvites: () =>
		request<{ invites: MyInvite[]; suggestions: TeamSuggestion[] }>(
			"/orgs/my-invites",
		),
	acceptMyInvite: (id: string) =>
		request<{ ok: true; orgId: string }>(`/orgs/my-invites/${id}/accept`, {
			method: "POST",
		}),
	invitePreview: (token: string) =>
		request<InvitePreview>(`/orgs/invites/${encodeURIComponent(token)}`),
	acceptInviteToken: (token: string) =>
		request<{ ok: true; orgId: string }>("/orgs/invites/accept", {
			method: "POST",
			body: JSON.stringify({ token }),
		}),
	joinSuggestedTeam: (orgId: string) =>
		request<{ ok: true; joined: boolean; requested?: boolean; orgId?: string }>(
			`/orgs/suggestions/${orgId}/join`,
			{ method: "POST" },
		),
	/** b2c: set the session's active team (Clerk org) so new docs and
	 *  billing attribute to it. Pass null to clear. */
	setActiveOrg: (orgId: string | null) =>
		request<{ activeOrgId: string | null }>("/session/active-org", {
			method: "POST",
			body: JSON.stringify({ orgId }),
		}),
	billing: () => request<BillingStatus>("/billing"),
	billingUsage: () => request<BillingUsage>("/billing/usage"),
	billingCatalog: () => request<BillingCatalog>("/billing/catalog"),
	billingMembers: () => request<{ members: MemberUsage[] }>("/billing/members"),
	billingInvoices: () => request<{ invoices: Invoice[] }>("/billing/invoices"),
	billingPortal: () =>
		request<{ url: string }>("/billing/portal", { method: "POST" }),
	/** Subscribe to / switch to a paid tier. Returns a Checkout URL when there's
	 *  no active subscription, or { changed: true } for an in-place plan swap. */
	billingSubscribe: (planId: string) =>
		request<{ url?: string; ok?: boolean; changed?: boolean }>(
			"/billing/subscribe",
			{ method: "POST", body: JSON.stringify({ planId }) },
		),
	/** Cancel a scheduled (deferred-downgrade) plan change — stay on the current plan. */
	billingCancelScheduledChange: () =>
		request<{ ok: true }>("/billing/cancel-scheduled-change", {
			method: "POST",
		}),
	/** Top up usage credits by a custom dollar amount — returns a Checkout URL. */
	billingCreditsCheckout: (usd: number) =>
		request<{ url: string }>("/billing/credits/checkout", {
			method: "POST",
			body: JSON.stringify({ usd }),
		}),
	/** Configure auto top-up (auto-reload). */
	billingAutoReload: (cfg: {
		enabled: boolean;
		thresholdTokens?: number | null;
		amountUsd?: string | number | null;
		monthlyCapUsd?: string | number | null;
	}) =>
		request<{ ok: true }>("/billing/credits/auto-reload", {
			method: "POST",
			body: JSON.stringify(cfg),
		}),
	deleteAccount: () => request<void>("/account", { method: "DELETE" }),
	notifications: () =>
		request<{ notifications: AppNotification[]; unreadCount: number }>(
			"/notifications",
		),
	markNotificationsRead: () =>
		request<void>("/notifications/read", { method: "POST" }),
	listDocs: (query?: Partial<DocListQuery>) => {
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(query ?? {})) {
			if (value !== undefined) params.set(key, String(value));
		}
		return request<DocList>(`/docs${params.size ? `?${params}` : ""}`);
	},
	chatHistory: (id: string, before?: string) =>
		request<ChatHistoryPage>(`/docs/${id}/chat-history${before ? `?before=${encodeURIComponent(before)}` : ""}`),
	/** Archive this deck's conversation and start a fresh one. The deck is
	 *  untouched; the agent begins the next turn with no prior context. */
	startNewChat: (id: string) =>
		request<{ archivedMessages: number; hadSession: boolean }>(
			`/docs/${id}/chat/new`,
			{ method: "POST" },
		),
	/** Condense what the assistant remembers of this conversation. The chat
	 *  log is unchanged — every message stays readable. */
	compactChat: (id: string) =>
		request<{
			compacted: boolean;
			beforeTokens: number | null;
			afterTokens: number | null;
		}>(`/docs/${id}/chat/compact`, { method: "POST" }),
	/** Previous conversations on this deck, kept verbatim, oldest first. */
	archivedChat: (id: string) =>
		request<{ messages: ArchivedChatMessage[] }>(`/docs/${id}/chat/archived`),
	/** Delete a message and everything said after it, rewinding what the agent
	 *  remembers to match. */
	deleteChatMessage: (id: string, messageId: string) =>
		request<{
			outcome: "rewound" | "session_cleared" | "memory_kept";
			deletedMessages: number;
		}>(`/docs/${id}/chat/messages/${messageId}`, { method: "DELETE" }),
	createDoc: (input: CreateDocInput) =>
		request<Doc>("/docs", { method: "POST", body: JSON.stringify(input) }),
	updateDoc: (id: string, input: UpdateDocInput) =>
		request<Doc>(`/docs/${id}`, {
			method: "PATCH",
			body: JSON.stringify(input),
		}),
	getDoc: (id: string) => request<DocWithRole>(`/docs/${id}`),
	/** The doc's derived slide index (per-slide decks): stable keys in
	 *  position order; empty for monolithic docs. Used to translate the
	 *  viewer's slide position into the `currentSlideKey` turn pointer. */
	getSlideIndex: (id: string) =>
		request<{
			slides: Array<{
				key: string;
				position: number;
				kind: string;
				title: string | null;
				summary: string | null;
			}>;
		}>(`/docs/${id}/slides`),
	/** Set a slide's speaker notes (editors). Empty text removes them. The
	 *  notes live in the slide's markup, so the saved deck arrives on the
	 *  stream as any other write; this answers once it is committed. */
	setSlideNotes: (id: string, slideKey: string, text: string) =>
		request<{ ok: true; changed: boolean; text: string }>(
			`/docs/${id}/slides/${encodeURIComponent(slideKey)}/notes`,
			{ method: "PUT", body: JSON.stringify({ text }) },
		),
	deleteDoc: (id: string) => request<void>(`/docs/${id}`, { method: "DELETE" }),
	/** Star / un-star a deck for the signed-in user. Idempotent in both
	 *  directions so the optimistic toggle can fire on every click without
	 *  the caller tracking in-flight state. */
	setDocFavourite: (id: string, favourite: boolean) =>
		request<{ isFavourite: boolean }>(`/docs/${id}/favourite`, {
			method: favourite ? "PUT" : "DELETE",
		}),
	/** Archive / restore a deck. Idempotent both ways; owner or team admin. */
	setDocArchived: (id: string, archived: boolean) =>
		request<{ isArchived: boolean }>(`/docs/${id}/archive`, {
			method: archived ? "PUT" : "DELETE",
		}),
	/** File a deck into a team folder, or unfile it with null. */
	setDocFolder: (id: string, folderId: string | null) =>
		request<{ folderId: string | null }>(`/docs/${id}/folder`, {
			method: "PUT",
			body: JSON.stringify({ folderId }),
		}),
	/** The deck's first slide as a standalone document, for the list's grid
	 *  view. `html` is null for a deck with no slides yet — a normal state, so
	 *  the caller draws a placeholder rather than treating it as an error. */
	docCover: (id: string) =>
		request<{ format: DocFormat; html: string | null }>(`/docs/${id}/cover`),
	listFolders: () => request<{ folders: DeckFolder[] }>("/folders"),
	createFolder: (name: string) =>
		request<DeckFolder>("/folders", {
			method: "POST",
			body: JSON.stringify({ name }),
		}),
	renameFolder: (id: string, name: string) =>
		request<DeckFolder>(`/folders/${id}`, {
			method: "PATCH",
			body: JSON.stringify({ name }),
		}),
	deleteFolder: (id: string) =>
		request<void>(`/folders/${id}`, { method: "DELETE" }),
	sendTurn: (id: string, input: SendTurnInput) =>
		request<{ ok: true }>(`/docs/${id}/turns`, {
			method: "POST",
			body: JSON.stringify(input),
		}),
	/** Cancel the doc's running agent turn (and drop any queued ones). */
	cancelTurns: (id: string) =>
		request<{ ok: true; cancelled: number }>(`/docs/${id}/turns/cancel`, {
			method: "POST",
		}),
	setTyping: (id: string, isTyping: boolean, tabId: string) =>
		request<void>(`/docs/${id}/typing`, {
			method: "POST",
			body: JSON.stringify({ isTyping, tabId }),
		}),
	/** Report which slide this tab is showing (per-slide viewer indicators). */
	setViewing: (id: string, tabId: string, slideKey: string | null, seq: number) =>
		request<void>(`/docs/${id}/viewing`, {
			method: "POST",
			body: JSON.stringify({ tabId, slideKey, seq, at: Date.now() }),
		}),
	listAccess: (id: string) =>
		request<{ users: AccessUser[]; groups: AccessGroup[] }>(
			`/docs/${id}/access`,
		),
	share: (id: string, input: ShareInput) =>
		request<AccessUser>(`/docs/${id}/access`, {
			method: "POST",
			body: JSON.stringify(input),
		}),
	shareGroup: (id: string, input: ShareGroupInput) =>
		request<AccessGroup>(`/docs/${id}/access/groups`, {
			method: "POST",
			body: JSON.stringify(input),
		}),
	revokeAccess: (id: string, userId: string) =>
		request<void>(`/docs/${id}/access/${userId}`, { method: "DELETE" }),
	/** Change an existing collaborator's role (owner only). Not a re-share —
	 *  it sends no notification. */
	setAccessRole: (id: string, userId: string, role: "editor" | "viewer") =>
		request<AccessUser>(`/docs/${id}/access/${userId}`, {
			method: "PATCH",
			body: JSON.stringify({ role }),
		}),
	setGroupAccessRole: (
		id: string,
		groupId: string,
		role: "editor" | "viewer",
	) =>
		request<AccessGroup>(
			`/docs/${id}/access/groups/${encodeURIComponent(groupId)}`,
			{ method: "PATCH", body: JSON.stringify({ role }) },
		),
	transferDoc: (id: string, userId: string) =>
		request<{ ok: true }>(`/docs/${id}/transfer`, {
			method: "POST",
			body: JSON.stringify({ userId }),
		}),
	/** Direct download URL for a deck's raw HTML (cookie-authenticated, same origin). */
	exportDocUrl: (id: string, view?: DeckView) =>
		`${BASE}/docs/${id}/export${viewQuery(view)}`,
	revokeGroupAccess: (id: string, groupId: string) =>
		request<void>(`/docs/${id}/access/groups/${encodeURIComponent(groupId)}`, {
			method: "DELETE",
		}),
	searchUsers: (q: string, docId?: string) =>
		request<{
			users: SearchedUser[];
			groups: SearchedGroup[];
			directoryReachable: boolean;
		}>(
			`/users/search?q=${encodeURIComponent(q)}${docId ? `&docId=${docId}` : ""}`,
		),
	/** Empty-state share suggestions: top team members to share this doc with. */
	shareSuggestions: (docId: string) =>
		request<{ users: SearchedUser[] }>(`/docs/${docId}/access/suggestions`),
	listVersions: (id: string) =>
		request<{ versions: DocVersion[] }>(`/docs/${id}/versions`),
	compareVersion: (id: string, versionId: string) =>
		request<DocCompareResult>(`/docs/${id}/versions/${versionId}/compare`),
	restoreVersion: (id: string, versionId: string) =>
		request<{ id: string; restoredAt: string }>(
			`/docs/${id}/versions/${versionId}/restore`,
			{ method: "POST" },
		),
	/** Inline text edits made in the preview. Applied at once on an idle
	 *  deck; `queued` while an agent turn runs (the outcome then arrives as a
	 *  `text_edits_resolved` stream event). */
	applyTextEdits: (id: string, input: TextEditsRequest) =>
		request<TextEditsResponse>(`/docs/${id}/text-edits`, {
			method: "POST",
			body: JSON.stringify(input),
		}),
	/** Recorded status of the given edits — how a client that missed the
	 *  transient `text_edits_resolved` event (a dropped stream) catches up. */
	textEditStatuses: (id: string, ids: string[]) =>
		request<TextEditsResponse>(
			`/docs/${id}/text-edits?ids=${encodeURIComponent(ids.join(","))}`,
		),
	/** This person's edits still waiting on a running turn — what a page
	 *  reloaded mid-turn puts back on screen and keeps asking after. */
	pendingTextEdits: (id: string) =>
		request<PendingTextEditsResponse>(`/docs/${id}/text-edits/pending`),
	// ---- b2c first-run brand onboarding -------------------------------------
	/** Current team's brand + onboarding status. Drives the onboarding gate. */
	getBrandProfile: () => request<BrandProfileResponse>("/brand/profile"),
	/** Process a website and/or uploaded files (multipart) into a saved brand.
	 *  Returns a job id to poll; the brand is saved server-side (no review). */
	processBrand: (form: FormData) =>
		requestForm<{ id: string }>("/brand/process", form),
	/** Poll a processing job. */
	getBrandJob: (id: string, signal?: AbortSignal) =>
		request<BrandJobResponse>(`/brand/jobs/${id}`, { signal }),
	/** Supply a logo (multipart) when processing couldn't find one. */
	uploadBrandLogo: (form: FormData) =>
		requestForm<BrandProfile>("/brand/logo", form),
	/** Save brand edits from the settings page. */
	saveBrandProfile: (input: SaveBrandProfileInput) =>
		request<BrandProfile>("/brand/profile", {
			method: "PUT",
			body: JSON.stringify(input),
		}),
	/** Skip onboarding: white-label the MakerX template under a company name. */
	skipOnboarding: (companyName: string) =>
		request<BrandProfile>("/brand/skip", {
			method: "POST",
			body: JSON.stringify({ companyName }),
		}),
	/** Ensure the team's welcome deck exists (idempotent) and get its id. The
	 *  build normally already started server-side when the scan succeeded; this
	 *  is how the finishing screen finds where to navigate. Null when no deck
	 *  applies (e.g. the team already has other decks). */
	ensureWelcomeDeck: () =>
		request<{ docId: string | null }>("/brand/welcome-deck", {
			method: "POST",
		}),
	/** Team-facing brand source management + re-run (shares the admin service). */
	brandAssets: () =>
		request<{
			assets: AdminBrandAsset[];
			websiteUrl: string | null;
			brandStatus: string | null;
			hasProfile: boolean;
			processing: { id: string; status: string } | null;
			canEdit: boolean;
		}>("/brand/assets"),
	addBrandAssets: (form: FormData) =>
		requestForm<{ ok: true; added: number }>("/brand/assets", form),
	deleteBrandAsset: (assetId: string) =>
		request<{ ok: true }>(`/brand/assets/${assetId}`, { method: "DELETE" }),
	reprocessBrand: (url?: string) =>
		request<{ id: string }>("/brand/reprocess", {
			method: "POST",
			body: JSON.stringify({ url }),
		}),
	// ---- Brand Studio: template groups + canvas docs --------------------------
	/** The styles a new deck can be built from — empty when the team has
	 *  none (or the edition has no teams); the picker then doesn't render. */
	deckStyles: () => request<{ styles: DeckStyle[] }>("/brand/deck-styles"),
	/** The styles THIS deck can be built in (its own team's — a deck shared
	 *  across teams must not be offered the viewer's team's groups). */
	docStyles: (docId: string, theme: "dark" | "light" = "dark") =>
		request<{ styles: DeckStyle[] }>(`/docs/${docId}/styles?theme=${theme}`),
	/** Groups + their template listings (seeds default groups lazily). */
	brandGroups: () => request<BrandGroupsResponse>("/brand/groups"),
	/** Standalone preview documents (one per template) for LIVE gallery tiles.
	 *  Separate from the groups listing so that stays lean. */
	groupPreviews: (groupId: string, theme: "dark" | "light" = "dark") =>
		request<{
			previews: {
				id: string;
				key: string;
				format: "landscape" | "portrait";
				html: string;
			}[];
		}>(`/brand/groups/${groupId}/previews?theme=${theme}`),
	/** Save a deck as a slide library — its own slides, kept for the deck
	 *  assistant to pull from — or refresh the one already saved from it. */
	saveDeckAsLibrary: (input: { docId: string; name?: string }) =>
		request<{
			group: Omit<StudioGroup, "canvasDocId" | "templates">;
			templates: Array<{ key: string; name: string }>;
			refreshed: boolean;
		}>("/brand/libraries", {
			method: "POST",
			body: JSON.stringify(input),
		}),
	createBrandGroup: (input: { name: string; description?: string | null }) =>
		request<{ group: StudioGroup }>("/brand/groups", {
			method: "POST",
			body: JSON.stringify(input),
		}),
	patchBrandGroup: (
		id: string,
		input: { name?: string; description?: string | null; sortOrder?: number },
	) =>
		request<{ group: StudioGroup }>(`/brand/groups/${id}`, {
			method: "PATCH",
			body: JSON.stringify(input),
		}),
	/** Ensure + (re)materialise the group's canvas doc; returns its doc id. */
	ensureGroupCanvas: (id: string) =>
		request<{ docId: string; group: StudioGroup }>(
			`/brand/groups/${id}/canvas`,
			{ method: "POST" },
		),
	groupVersions: (id: string) =>
		request<{ versions: StudioVersion[] }>(`/brand/groups/${id}/versions`),
	restoreGroupVersion: (id: string, versionId: string) =>
		request<{ id: string; restoredAt: string }>(
			`/brand/groups/${id}/versions/${versionId}/restore`,
			{ method: "POST" },
		),
	moveTemplate: (id: string, groupId: string, sortOrder?: number) =>
		request<{ ok: true }>(`/brand/templates/${id}/move`, {
			method: "POST",
			body: JSON.stringify({
				groupId,
				...(sortOrder !== undefined ? { sortOrder } : {}),
			}),
		}),
	// ---- Brand book (identity + guidelines) -----------------------------------
	/** The existing brand-book canvas doc id (member-readable; null if none). */
	brandBook: () => request<{ docId: string | null }>("/brand/book"),
	/** Ensure + (re)materialise the org's brand-book canvas (admin); returns id. */
	brandBookCanvas: () =>
		request<{ docId: string }>("/brand/book/canvas", { method: "POST" }),
	/** Identity + guidelines snapshot history. */
	brandBookVersions: () =>
		request<{
			versions: Array<{
				id: string;
				changeSummary: string | null;
				createdAt: string;
				createdBy: { id: string; name: string } | null;
			}>;
		}>("/brand/book/versions"),
	/** Download URL for the brand book as self-contained HTML (cookie-auth). */
	brandBookExportUrl: () => `${BASE}/brand/book/export`,
	// ---- Health, retokenise, materials + import -------------------------------
	brandHealth: () => request<BrandHealth>("/brand/health"),
	retokeniseGroup: (groupId: string) =>
		request<{ docId: string }>(`/brand/groups/${groupId}/retokenise`, {
			method: "POST",
		}),
	brandMaterials: () => request<BrandMaterials>("/brand/materials"),
	/** Keep files for every deck to draw on (multipart, `file` repeated). */
	uploadMaterials: (form: FormData) =>
		requestForm<MaterialsUploadResult>("/brand/materials", form),
	/** Download URL for one material (cookie-auth, served as an attachment). */
	materialDownloadUrl: (id: string) => `${BASE}/brand/materials/${id}/download`,
	deleteMaterial: (id: string) =>
		request<{ ok: true }>(`/brand/materials/${id}`, { method: "DELETE" }),
	/** Import a file with an intent (multipart). Returns a job id to poll. */
	importBrandFile: (form: FormData) =>
		requestForm<{ jobId: string }>("/brand/import", form),
	// ---- Org asset library (§6 phase 5) — distinct from /brand/assets, which
	// is the older brand-sources/reprocess surface. --------------------------
	brandAssetLibrary: () => request<BrandAssetLibrary>("/brand/library"),
	/** The asset's bytes, inline (cookie-auth): an <img> src or @font-face
	 *  url for the Materials page's previews. `version` is the asset's
	 *  updatedAt, so a same-name re-upload (same id) isn't served from cache. */
	libraryAssetUrl: (id: string, version: string) =>
		`${BASE}/brand/library/${id}?v=${encodeURIComponent(version)}`,
	uploadBrandAsset: (form: FormData) =>
		requestForm<{ name: string; token: string; family?: string }>(
			"/brand/library",
			form,
		),
	/** Remove a template group. Its templates are DETACHED, not deleted — they
	 *  re-classify into another group — but the group's canvas and version
	 *  history go with it. */
	/** Archive a template: removed from deck turns and its key freed for
	 *  re-import, but the html is kept. */
	archiveTemplate: (id: string) =>
		request<{ archived: string; key: string }>(`/brand/templates/${id}`, {
			method: "DELETE",
		}),
	deleteTemplateGroup: (id: string) =>
		request<{ deleted: string; templatesDetached: number }>(
			`/brand/groups/${id}`,
			{ method: "DELETE" },
		),
	deleteLibraryAsset: (id: string) =>
		request<{ ok: true }>(`/brand/library/${id}`, { method: "DELETE" }),
	/** The org's slide-template library + any in-progress build (poll). */
	getSlideTemplates: () => request<SlideTemplatesResponse>("/brand/templates"),
	/** Build the slide-template library from the team's example decks. */
	generateSlideTemplates: () =>
		request<{ id: string }>("/brand/templates/generate", { method: "POST" }),
	render: (id: string, view?: DeckView) =>
		request<RenderResult>(`/docs/${id}/render${viewQuery(view)}`, {
			method: "POST",
		}),
	renderPptx: (id: string, view?: DeckView) =>
		request<RenderResult>(`/docs/${id}/render/pptx${viewQuery(view)}`, {
			method: "POST",
		}),
	listRenders: (id: string) =>
		request<{ renders: RenderResult[] }>(`/docs/${id}/renders`),
};

export function loginUrl(returnTo = "/docs") {
	const q = new URLSearchParams({ returnTo });
	return `${BASE}/auth/login?${q.toString()}`;
}

export { ApiError };
